import {
  PigRunMapEntry,
  PigLocationState,
  PipelineSectionMarkerState,
} from '@/models/PigRunState';
import {
  TrackingWorksheet,
  TrackingWorksheetDistanceSource,
  TrackingWorksheetPassSource,
  TrackingWorksheetRow,
  TrackingWorksheetRowStatus,
} from '@/models/TrackingWorksheet';
import { VerifiedPassageSource } from '@/models/VerifiedPassageSource';

const MILLISECONDS_PER_SECOND = 1000;
const EARTH_RADIUS_METERS = 6371000;

function cloneDate(date: Date): Date {
  return new Date(date.getTime());
}

function parseUtcDateTime(value: string | null | undefined): Date | null {
  if (!value) return null;

  const normalizedValue = value.trim();
  const sqlDateMatch = normalizedValue.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?Z?$/
  );
  if (sqlDateMatch) {
    const [, year, month, day, hour, minute, second, fraction = ''] =
      sqlDateMatch;
    const milliseconds = Number(fraction.slice(0, 3).padEnd(3, '0'));
    const parsed = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        milliseconds
      )
    );
    if (
      parsed.getUTCFullYear() === Number(year) &&
      parsed.getUTCMonth() === Number(month) - 1 &&
      parsed.getUTCDate() === Number(day) &&
      parsed.getUTCHours() === Number(hour) &&
      parsed.getUTCMinutes() === Number(minute) &&
      parsed.getUTCSeconds() === Number(second)
    ) {
      return parsed;
    }
    return null;
  }

  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})$/i.test(
      normalizedValue
    )
  ) {
    return null;
  }

  const timestamp = Date.parse(normalizedValue);
  return Number.isNaN(timestamp) ? null : new Date(timestamp);
}

function parseEtaDurationSeconds(
  value: string | number | null | undefined
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
  }
  if (!value) return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const numericSeconds = Number(trimmedValue);
  if (Number.isFinite(numericSeconds) && numericSeconds >= 0) {
    return Math.round(numericSeconds);
  }

  const timeSpanMatch = trimmedValue.match(
    /^(?:(\d+)\.)?(\d{1,2}):([0-5]\d):([0-5]\d)(?:\.\d{1,7})?$/
  );
  if (!timeSpanMatch) return null;

  const [, days = '0', hours, minutes, seconds] = timeSpanMatch;
  const parsedHours = Number(hours);
  if (parsedHours > 23) return null;

  return (
    Number(days) * 86400 +
    parsedHours * 3600 +
    Number(minutes) * 60 +
    Number(seconds)
  );
}

function resolveEtaUtc(
  value: string | number | null | undefined,
  serverTimeUtc: Date | null
): Date | null {
  const durationSeconds = parseEtaDurationSeconds(value);
  if (durationSeconds !== null && serverTimeUtc) {
    return new Date(
      serverTimeUtc.getTime() + durationSeconds * MILLISECONDS_PER_SECOND
    );
  }

  return typeof value === 'string' ? parseUtcDateTime(value) : null;
}

function uniqueLinkedUids(marker: PipelineSectionMarkerState): number[] {
  return [
    ...new Set((marker.manualLinkDevices || []).map((device) => device.uid)),
  ]
    .filter((uid) => Number.isFinite(uid) && uid > 0)
    .sort((left, right) => left - right);
}

function isNonNegativeFiniteNumber(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value >= 0;
}

function hasValidCoordinates(marker: PipelineSectionMarkerState): boolean {
  return (
    Number.isFinite(marker.latitude) &&
    Number.isFinite(marker.longitude) &&
    marker.latitude >= -90 &&
    marker.latitude <= 90 &&
    marker.longitude >= -180 &&
    marker.longitude <= 180
  );
}

function haversineDistanceMeters(
  from: PipelineSectionMarkerState,
  to: PipelineSectionMarkerState
): number | null {
  if (!hasValidCoordinates(from) || !hasValidCoordinates(to)) return null;

  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(to.latitude - from.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const a =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(longitudeDelta / 2) ** 2;

  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function measuredPipelineDistance(
  marker: PipelineSectionMarkerState
): number | null {
  const value = marker.pipelineDistance;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function hasValidPipelineStationing(
  markers: PipelineSectionMarkerState[]
): boolean {
  if (markers.length === 0) return false;

  let previousDistance: number | null = null;
  for (let index = 0; index < markers.length; index += 1) {
    const pipelineDistance = measuredPipelineDistance(markers[index]);
    if (pipelineDistance === null) return false;

    if (index === 0) {
      if (pipelineDistance !== 0) return false;
    } else if (
      previousDistance === null ||
      pipelineDistance <= previousDistance
    ) {
      return false;
    }

    previousDistance = pipelineDistance;
  }

  return true;
}

function measuredLegDistanceMeters(
  from: PipelineSectionMarkerState,
  to: PipelineSectionMarkerState
): number | null {
  const fromDistance = measuredPipelineDistance(from);
  const toDistance = measuredPipelineDistance(to);
  return fromDistance === null || toDistance === null
    ? null
    : toDistance - fromDistance;
}

function selectedSourcesByMarkerId(
  sources: VerifiedPassageSource[]
): Map<number, VerifiedPassageSource> {
  const selectedSources = new Map<number, VerifiedPassageSource>();
  for (const source of sources) {
    if (!selectedSources.has(source.pipelineSectionMarkerId)) {
      selectedSources.set(source.pipelineSectionMarkerId, source);
    }
  }
  return selectedSources;
}

function isTrackMarker(marker: PipelineSectionMarkerState): boolean {
  return marker.trackState === 'T';
}

function buildPassSource(
  source: VerifiedPassageSource | undefined
): TrackingWorksheetPassSource | null {
  if (!source) return null;
  if (source.isManual) return { kind: 'manual', uid: null };
  if (source.uid !== null && source.uid > 0) {
    return { kind: 'agm', uid: source.uid };
  }
  return null;
}

function findNextTrackMarkerIndex(
  markers: PipelineSectionMarkerState[]
): number {
  let lastPassedIndex = -1;
  for (let index = 0; index < markers.length; index += 1) {
    if (isTrackMarker(markers[index]) && markers[index].hasVerifiedPassage) {
      lastPassedIndex = index;
    }
  }

  for (let index = lastPassedIndex + 1; index < markers.length; index += 1) {
    if (isTrackMarker(markers[index]) && !markers[index].hasVerifiedPassage) {
      return index;
    }
  }

  return -1;
}

function getRowStatus(
  marker: PipelineSectionMarkerState,
  index: number,
  nextTrackMarkerIndex: number,
  isCompletedRun: boolean
): TrackingWorksheetRowStatus {
  if (!isTrackMarker(marker)) return 'skipped';
  if (marker.hasVerifiedPassage) return 'passed';
  if (isCompletedRun) return 'not-verified';
  if (index === nextTrackMarkerIndex) return 'next';
  return 'pending';
}

function buildRows(
  markers: PipelineSectionMarkerState[],
  sourcesByMarkerId: Map<number, VerifiedPassageSource>,
  isCompletedRun: boolean,
  distanceFromLaunchSource: TrackingWorksheetDistanceSource
): TrackingWorksheetRow[] {
  const nextTrackMarkerIndex = isCompletedRun
    ? -1
    : findNextTrackMarkerIndex(markers);
  let cumulativeGpsDistanceMeters: number | null = 0;

  return markers.map((marker, index) => {
    const source = marker.hasVerifiedPassage
      ? sourcesByMarkerId.get(marker.pipelineSectionMarkerId)
      : undefined;
    const previousMarker = index > 0 ? markers[index - 1] : undefined;
    const gpsLegDistanceMeters = previousMarker
      ? haversineDistanceMeters(previousMarker, marker)
      : null;
    const measuredLegMeters = previousMarker
      ? measuredLegDistanceMeters(previousMarker, marker)
      : null;
    const legDistanceMeters =
      distanceFromLaunchSource === 'measured'
        ? measuredLegMeters
        : gpsLegDistanceMeters;

    if (distanceFromLaunchSource === 'gps' && index > 0) {
      cumulativeGpsDistanceMeters =
        cumulativeGpsDistanceMeters === null || gpsLegDistanceMeters === null
          ? null
          : cumulativeGpsDistanceMeters + gpsLegDistanceMeters;
    }

    const distanceFromLaunchMeters =
      distanceFromLaunchSource === 'measured'
        ? measuredPipelineDistance(marker)
        : cumulativeGpsDistanceMeters;
    const hasValidLocation = hasValidCoordinates(marker);

    return {
      pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
      sortOrder: marker.sortOrder,
      markerName: marker.name,
      description: marker.description,
      trackState: marker.trackState,
      status: getRowStatus(marker, index, nextTrackMarkerIndex, isCompletedRun),
      linkedUids: uniqueLinkedUids(marker),
      passSource: buildPassSource(source),
      passageTimeUtc: source ? parseUtcDateTime(source.passageTimeStamp) : null,
      etaUtc: null,
      countdownSeconds: null,
      legDistanceMeters,
      legDistanceSource:
        legDistanceMeters === null ? null : distanceFromLaunchSource,
      distanceFromLaunchMeters,
      legSpeedMetersPerSecond: null,
      runAverageSpeedMetersPerSecond: null,
      latitude: hasValidLocation ? marker.latitude : null,
      longitude: hasValidLocation ? marker.longitude : null,
    };
  });
}

function applyEta(row: TrackingWorksheetRow, etaUtc: Date, nowUtc: Date): void {
  if (row.status !== 'next' && row.status !== 'pending') return;

  row.etaUtc = cloneDate(etaUtc);
  row.countdownSeconds = Math.max(
    0,
    Math.ceil((etaUtc.getTime() - nowUtc.getTime()) / MILLISECONDS_PER_SECOND)
  );
}

function applyLiveEtas(
  rows: TrackingWorksheetRow[],
  pigLocation: PigLocationState | null,
  nowUtc: Date
): Date | null {
  const serverTimeUtc = parseUtcDateTime(pigLocation?.serverTime);
  const nextTrackEta = pigLocation?.etaToNextTrack;
  const nextEtaUtc = resolveEtaUtc(
    nextTrackEta === undefined || nextTrackEta === ''
      ? pigLocation?.etaToNext
      : nextTrackEta,
    serverTimeUtc
  );
  const endEtaUtc = resolveEtaUtc(pigLocation?.etaToEnd, serverTimeUtc);
  const nextIndex = rows.findIndex((row) => row.status === 'next');
  if (nextIndex >= 0 && nextEtaUtc) {
    applyEta(rows[nextIndex], nextEtaUtc, nowUtc);

    const speed = pigLocation?.speed;
    if (Number.isFinite(speed) && speed !== undefined && speed > 0) {
      let projectedEtaUtc = nextEtaUtc;
      for (let index = nextIndex + 1; index < rows.length; index += 1) {
        const row = rows[index];
        if (row.legDistanceMeters === null) break;

        projectedEtaUtc = new Date(
          projectedEtaUtc.getTime() +
            (row.legDistanceMeters / speed) * MILLISECONDS_PER_SECOND
        );
        if (row.status === 'next' || row.status === 'pending') {
          applyEta(row, projectedEtaUtc, nowUtc);
        }
      }
      rows[nextIndex].legSpeedMetersPerSecond = speed;
    }
  }

  const lastUpcomingRow = [...rows]
    .reverse()
    .find((row) => row.status === 'next' || row.status === 'pending');
  if (lastUpcomingRow && endEtaUtc) {
    applyEta(lastUpcomingRow, endEtaUtc, nowUtc);
  }

  return endEtaUtc;
}

function applyPassageSpeeds(
  rows: TrackingWorksheetRow[],
  launchTimeUtc: Date | null
): number | null {
  let previousPassedRow: TrackingWorksheetRow | null = null;
  let distanceSincePreviousPassMeters: number | null = 0;
  let lastPassedRow: TrackingWorksheetRow | null = null;
  let hasPassedMarker = false;

  for (const row of rows) {
    if (previousPassedRow) {
      distanceSincePreviousPassMeters =
        distanceSincePreviousPassMeters === null ||
        row.legDistanceMeters === null
          ? null
          : distanceSincePreviousPassMeters + row.legDistanceMeters;
    }

    if (row.status !== 'passed') continue;
    if (!row.passageTimeUtc) {
      previousPassedRow = null;
      distanceSincePreviousPassMeters = null;
      hasPassedMarker = true;
      continue;
    }

    if (previousPassedRow && distanceSincePreviousPassMeters !== null) {
      const durationSeconds =
        (row.passageTimeUtc.getTime() -
          previousPassedRow.passageTimeUtc!.getTime()) /
        MILLISECONDS_PER_SECOND;
      if (durationSeconds > 0 && distanceSincePreviousPassMeters > 0) {
        row.legSpeedMetersPerSecond =
          distanceSincePreviousPassMeters / durationSeconds;
      }
    } else if (
      !hasPassedMarker &&
      launchTimeUtc &&
      row.distanceFromLaunchMeters !== null
    ) {
      const durationSeconds =
        (row.passageTimeUtc.getTime() - launchTimeUtc.getTime()) /
        MILLISECONDS_PER_SECOND;
      if (durationSeconds > 0 && row.distanceFromLaunchMeters > 0) {
        row.legSpeedMetersPerSecond =
          row.distanceFromLaunchMeters / durationSeconds;
      }
    }

    previousPassedRow = row;
    distanceSincePreviousPassMeters = 0;
    lastPassedRow = row;
    hasPassedMarker = true;
  }

  if (
    !launchTimeUtc ||
    !lastPassedRow?.passageTimeUtc ||
    lastPassedRow.distanceFromLaunchMeters === null ||
    lastPassedRow.distanceFromLaunchMeters <= 0
  ) {
    return null;
  }

  const durationSeconds =
    (lastPassedRow.passageTimeUtc.getTime() - launchTimeUtc.getTime()) /
    MILLISECONDS_PER_SECOND;
  return durationSeconds > 0
    ? lastPassedRow.distanceFromLaunchMeters / durationSeconds
    : null;
}

/**
 * Builds the worksheet view model from canonical active-run state. The caller
 * supplies the clock so countdown calculations stay deterministic and testable.
 */
export function buildTrackingWorksheet(
  entry: PigRunMapEntry,
  now: Date
): TrackingWorksheet {
  if (Number.isNaN(now.getTime())) {
    throw new Error('Tracking worksheet requires a valid clock value.');
  }

  const builtAtUtc = cloneDate(now);
  const markers = [...entry.markers].sort(
    (left, right) => left.sortOrder - right.sortOrder
  );
  const isCompletedRun = entry.pigRun.lifecycleState === 'D';
  const sourcesByMarkerId = selectedSourcesByMarkerId(
    entry.verifiedPassageSources || []
  );
  const launchTimeUtc = parseUtcDateTime(entry.pigRun.launchDate);
  const distanceFromLaunchSource = hasValidPipelineStationing(markers)
    ? 'measured'
    : 'gps';
  const rows = buildRows(
    markers,
    sourcesByMarkerId,
    isCompletedRun,
    distanceFromLaunchSource
  );
  const endEtaUtc = isCompletedRun
    ? null
    : applyLiveEtas(rows, entry.pigLocation, builtAtUtc);
  const runAverageSpeedMetersPerSecond = applyPassageSpeeds(
    rows,
    launchTimeUtc
  );
  for (const row of rows) {
    row.runAverageSpeedMetersPerSecond = runAverageSpeedMetersPerSecond;
  }

  const nextRow = rows.find((row) => row.status === 'next') ?? null;
  const currentSpeed = entry.pigLocation?.speed;
  const serverTimeUtc = parseUtcDateTime(entry.pigLocation?.serverTime);
  return {
    summary: {
      pigRunId: entry.pigRun.pigRunId,
      runName: entry.pigRun.name,
      jobNumber: entry.pigRun.jobNumber?.trim() || null,
      pipelineName: null,
      lifecycleState: entry.pigRun.lifecycleState,
      launchTimeUtc,
      receiveTimeUtc: null,
      currentSpeedMetersPerSecond: isNonNegativeFiniteNumber(currentSpeed)
        ? currentSpeed
        : null,
      verifiedMarkerCount: markers.filter((marker) => marker.hasVerifiedPassage)
        .length,
      markerCount: markers.length,
      deviceCount: entry.pigRun.devices?.length ?? null,
      passageCount: null,
      verifiedPassageCount: null,
      nextMarkerName: nextRow?.markerName ?? null,
      nextMarkerEtaUtc: nextRow?.etaUtc ?? null,
      endEtaUtc,
      lastUpdatedUtc: serverTimeUtc,
    },
    distanceFromLaunchSource,
    rows,
    builtAtUtc,
  };
}

export { parseEtaDurationSeconds, parseUtcDateTime, resolveEtaUtc };
